import { useState, useCallback, useMemo } from "react";
import { useMutation } from "@tanstack/react-query";
import {
  AssignSupplierService,
  OrderValidationService,
} from "../assignSupplierUtils";
import { OrderItem } from "../orderUtils";
import useNotification from "../../../utils/useNotification";
import { useGaraazToastWithChildren } from "../../../utils/useNotification";
import { OrdersTextLabels } from "../enums";
import OrderStatusChangeErrorBox from "../components/OrderStatusChangeErrorBox";
import {
  parseCptHoursOffset,
  calculateCPTForSlab,
} from "../../../components/SupplierInventorySearch/utils/cptCalculationUtils";
import { useGetOrderMediaConfig } from "../../PlaceOrderTest/queryHooks";
import { EstimateAllocatorService } from "../services/EstimateAllocatorService";
import { useOrdersStore } from "../store/useOrdersStore";
import {
  EnrichedSupplierForDelivery,
  NormalizedDeliverySelection,
} from "../../../components/DeliveryOptions/types";

interface UseAssignSupplierProps {
  orderId: string;
  orderData: any;
  orderItems: OrderItem[];
  setOrderMetaInfoWithOrderId: (orderId: string, metaInfo: any) => void;
  refetchAllOrders: () => void;
  isCustomerOrderFreezed?: boolean;
}
export const useExecuteSupplierBucket = ({
  orderId,
  orderData,
  orderItems,
  setOrderMetaInfoWithOrderId,
  refetchAllOrders,
  isCustomerOrderFreezed,
}: UseAssignSupplierProps) => {
  const [isExecuting, setIsExecuting] = useState(false);
  const toast = useNotification();
  const garaazToast = useGaraazToastWithChildren();
  const { data: mobileConfigs } = useGetOrderMediaConfig();
  const orderEta = useOrdersStore(
    useCallback((state) => state.orderEtas[orderId], [orderId])
  );

  const blockStatus = useMemo(
    () => OrderValidationService.checkOrderBlockStatus(orderData),
    [orderData]
  );

  const isBlocked = blockStatus.isBlocked;

  const executeSupplierBucketMutation = useMutation({
    mutationFn: async ({
      supplierBucket,
      transactionType,
      creditNotePercentage,
      discount,
      splitSelections,
      cptGivenTime,
      splitData,
      estimateNo,
      estimateSplitMode,
    }: any): Promise<any> => {
      try {
        const cptHoursOffsetBySlab = parseCptHoursOffset(
          mobileConfigs?.logistics_cpt_hours_offset
        );

        const isRootOrder = orderData._id === orderData.rootOrderId;
        if (orderData?.requireEstimate && isRootOrder) {
          const allocationResult = await EstimateAllocatorService.allocate(
            {
              sourceOrder: orderData,
              suppliers: supplierBucket,
              transactionType, // Can be undefined for hold/cancel/buytime-only flows
              orderEta,
              orderItems,
              discount,
              creditNotePercentage,
              cptHoursOffsetBySlab,
              splitSelections,
              cptGivenTime,
              splitData,
              estimateNo,
              estimateSplitMode: estimateSplitMode || "assign",
            },
            isCustomerOrderFreezed ?? false
          );

          if (!allocationResult.success) {
            throw new Error(
              allocationResult.message || "Estimate allocation failed"
            );
          }

          return allocationResult;
        }

        let allocateSupplierOrderId = orderId;
        let allocateSupplierOrderData = orderData;
        let allocatedSupplierOrderItems = orderItems;

        for (const supplier of supplierBucket) {
          const effectiveAt = allocateSupplierOrderData?.orderEffectiveAt;
          const cpt =
            supplier?.cptSlab && effectiveAt
              ? calculateCPTForSlab(
                  effectiveAt,
                  supplier.cptSlab,
                  cptHoursOffsetBySlab
                )
              : null;

          const orderETA = cpt
            ? [
                {
                  etaGivenTime: new Date().toISOString(),
                  eta: cpt.toISOString(),
                  reason: "system",
                },
              ]
            : undefined;

          const responseFromOneSupplierAllocation =
            await AssignSupplierService.assignSupplierWithFulfillmentLogic(
              allocateSupplierOrderId,
              supplier.supplierId,
              allocateSupplierOrderData,
              allocatedSupplierOrderItems,
              supplier.selectedParts || [],
              setOrderMetaInfoWithOrderId,
              supplier.supplierName,
              transactionType,
              creditNotePercentage,
              discount,
              supplier.cptSlab,
              orderETA,
              orderEta
            );

          if (!responseFromOneSupplierAllocation.success) {
            throw new Error(
              responseFromOneSupplierAllocation.message ||
                responseFromOneSupplierAllocation.error ||
                "Supplier assignment failed"
            );
          }

          const newRemainingOrder =
            responseFromOneSupplierAllocation.newOrders?.remainingOrder;
          const isFullFulfillment =
            responseFromOneSupplierAllocation.isFullFulfillment;

          allocateSupplierOrderId = newRemainingOrder?._id;
          allocateSupplierOrderData = newRemainingOrder;
          allocatedSupplierOrderItems =
            responseFromOneSupplierAllocation.remainingOrderItems ||
            responseFromOneSupplierAllocation.newOrders?.remainingOrder
              ?.orderItems;

          if (isFullFulfillment || !newRemainingOrder) {
            break;
          }
        }

        return {
          success: true,
          message: "Bucket executed successfully",
        };
      } catch (error) {
        throw error;
      }
    },
    onSuccess: (allocationResult: any) => {
      garaazToast({
        title: "Processed Successfully",
        status: "success",
      });

      refetchAllOrders();
    },
    onError: (error: any) => {
      const errorFromResponse = error?.response?.data?.errorMsg;
      const title = !!errorFromResponse?.length
        ? errorFromResponse?.[0]?.title
        : error?.message || OrdersTextLabels.ACCESS_DENIED_ORDER_STATUS_INVALID;

      garaazToast({
        title: title,
        status: "error",
        render: errorFromResponse ? (
          <OrderStatusChangeErrorBox errorMsg={errorFromResponse} />
        ) : undefined,
      });
      refetchAllOrders();
    },
  });

  const executeSupplierBucket = useCallback(
    async (
      supplierBucket: any,
      transactionType?: string,
      creditNotePercentage?: number,
      discount?: number,
      splitSelections?: NormalizedDeliverySelection[],
      cptGivenTime?: string,
      splitData?: EnrichedSupplierForDelivery[],
      estimateNo?: string,
      estimateSplitMode?: "assign" | "reassign",
    ) => {
      if (isBlocked) {
        toast({
          description:
            blockStatus.reason || "Order is blocked from supplier assignment",
          status: "warning",
        });
        return;
      }

      setIsExecuting(true);
      try {
        await executeSupplierBucketMutation.mutateAsync({
          supplierBucket,
          transactionType,
          creditNotePercentage,
          discount,
          splitSelections,
          cptGivenTime,
          splitData,
          estimateNo,
          estimateSplitMode,
        });
      } catch (error) {
        console.error(error);
      } finally {
        setIsExecuting(false);
      }
    },
    [executeSupplierBucketMutation, isBlocked, blockStatus.reason, toast]
  );

  return {
    executeSupplierBucket,
    isExecuting: isExecuting || executeSupplierBucketMutation.isLoading,
    error: executeSupplierBucketMutation.error,
    isBlocked,
    blockStatus,
  };
};
